import React, { useMemo } from 'react';

/** ProgressRing — circular progress with a glowing stroke. Used for the $1M PAT countdown gauge. */
export const ProgressRing = ({
  value = 0, // 0-100
  size = 160,
  stroke = 10,
  color = '#39FF14',
  trackColor = 'rgba(255,255,255,0.08)',
  label,
  sublabel,
  valueText,
  testid,
}) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const dash = (pct / 100) * c;

  const uid = useMemo(() => Math.random().toString(36).slice(2, 8), []);

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }} data-testid={testid}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`ring-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.65" />
            <stop offset="100%" stopColor={color} stopOpacity="1" />
          </linearGradient>
          <filter id={`glow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.5" />
          </filter>
        </defs>
        {/* track */}
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        {/* glow underlay */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeOpacity="0.4"
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${c}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          filter={`url(#glow-${uid})`}
        />
        {/* main stroke */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={`url(#ring-${uid})`}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${c}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 900ms cubic-bezier(.2,.9,.2,1)' }}
        />
        {/* tick marks every 10% around the ring */}
        {Array.from({ length: 36 }).map((_, i) => {
          const angle = (i * 10 * Math.PI) / 180;
          const r1 = r + stroke / 2 + 2;
          const r2 = r1 + 3;
          const cx = size / 2 + Math.cos(angle - Math.PI / 2) * r1;
          const cy = size / 2 + Math.sin(angle - Math.PI / 2) * r1;
          const cx2 = size / 2 + Math.cos(angle - Math.PI / 2) * r2;
          const cy2 = size / 2 + Math.sin(angle - Math.PI / 2) * r2;
          return <line key={i} x1={cx} y1={cy} x2={cx2} y2={cy2} stroke="rgba(255,255,255,0.14)" strokeWidth="0.6" />;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {label && <div className="text-[9px] uppercase tracking-widest text-white/45">{label}</div>}
        <div className="display text-2xl font-semibold mt-0.5" style={{ color }}>
          {valueText ?? `${pct.toFixed(1)}%`}
        </div>
        {sublabel && <div className="text-[10px] text-white/50 mt-0.5 px-3 leading-tight">{sublabel}</div>}
      </div>
    </div>
  );
};

export default ProgressRing;
